import React, { useState } from 'react';
import { ImageOff } from 'lucide-react';
import { viewableImageUrl } from '../../lib/fileUrls';

const AVATAR_COLORS = ['#1A56DB', '#15803D', '#D97706', '#DC2626', '#7C3AED', '#0D9488'];

function getColorFromString(str) {
  if (!str) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase()
    : parts[0].charAt(0).toUpperCase();
}

/**
 * Returns true if url points to Cloudinary or our production API.
 */
function isResolvableUrl(url) {
  if (!url) return false;
  if (url.startsWith('http://') || url.startsWith('https://')) return true;
  if (url.startsWith('/')) return true;
  return false;
}

/**
 * SafeImage — Image with fallback.
 * Non-resolvable legacy URLs show "Evidencia no disponible".
 */
export default function SafeImage({
  src,
  type = 'evidence',
  fallbackName = '',
  fallbackId = '',
  alt = '',
  className = '',
  imgClassName = '',
  showFallback = true,
  fallbackIcon = null,
  onLoad,
  onError,
  ...props
}) {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // HEIC y otros formatos que el navegador no muestra se piden convertidos a Cloudinary.
  const resolvedUrl = isResolvableUrl(src) ? viewableImageUrl(src, 800) : null;

  if (!resolvedUrl || hasError) {
    if (!showFallback) return null;

    if (fallbackIcon) {
      return (
        <div className={`flex items-center justify-center bg-slate-100 ${className}`} title={alt || fallbackName}>
          {fallbackIcon}
        </div>
      );
    }

    const initials = getInitials(fallbackName);
    const bgColor = getColorFromString(fallbackId || fallbackName);

    return (
      <div className={`flex items-center justify-center font-bold text-white ${className}`}
        style={{ backgroundColor: bgColor }} title={alt || fallbackName}>
        {initials}
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${className}`}>
      {isLoading && <div className="absolute inset-0 bg-slate-100 animate-pulse" />}
      <img
        src={resolvedUrl}
        alt={alt || fallbackName}
        className={`${imgClassName} ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-200`}
        onLoad={(e) => { setIsLoading(false); setHasError(false); onLoad?.(e); }}
        onError={(e) => { setIsLoading(false); setHasError(true); onError?.(e); }}
        {...props}
      />
    </div>
  );
}

/**
 * SafeEvidenceImage — For evidence files.
 * Shows "Evidencia no disponible" placeholder on failure.
 */
export function SafeEvidenceImage({ src, alt, className, ...props }) {
  return (
    <SafeImage
      src={src}
      type="evidence"
      alt={alt}
      className={className}
      showFallback={true}
      fallbackIcon={
        <div className="text-center text-slate-400 py-2">
          <ImageOff size={20} className="mx-auto mb-1" />
          <p className="text-[10px]">Evidencia no disponible</p>
        </div>
      }
      {...props}
    />
  );
}

/**
 * SafeAvatar — For user/staff avatars.
 */
export function SafeAvatar({ src, name, id, size = 'md', className = '', ...props }) {
  const sizes = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-14 h-14 text-lg',
    xl: 'w-20 h-20 text-2xl',
  };

  return (
    <SafeImage
      src={src}
      type="staff"
      fallbackName={name}
      fallbackId={id}
      alt={name}
      className={`${sizes[size] || sizes.md} rounded-full flex-shrink-0 ${className}`}
      imgClassName="w-full h-full object-cover rounded-full"
      showFallback={true}
      {...props}
    />
  );
}
